import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { ArrowLeft, Play, Edit3, Trash2, CheckCircle2, XCircle, Clock, Inbox } from "lucide-react";
import { toast } from "sonner";

// Trigger types treated as "Gmail new email"
const GMAIL_TRIGGER_ALIASES = new Set([
  "gmail_new_email", "gmail.new_email", "new_email", "email_received",
  "gmail_received", "gmail.received", "email_new", "gmail_inbox",
  "new_gmail", "incoming_email", "email_incoming",
]);

export default function WorkflowDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [wf, setWf] = useState(null);
  const [execs, setExecs] = useState([]);
  const [executing, setExecuting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [testingTrigger, setTestingTrigger] = useState(false);
  const [triggerDebug, setTriggerDebug] = useState(null);

  const load = () => {
    api.get(`/workflows/${id}`).then((r) => setWf(r.data)).catch(() => toast.error("Introuvable"));
    api.get(`/workflows/${id}/executions`).then((r) => setExecs(r.data));
  };
  useEffect(() => { load(); }, [id]);

  const run = async () => {
    setExecuting(true);
    try {
      await api.post(`/workflows/${id}/execute`);
      toast.success("Exécution terminée");
      load();
    } catch {
      toast.error("Exécution échouée");
    } finally {
      setExecuting(false);
    }
  };

  const toggleActive = async () => {
    if (!wf || toggling) return;
    setToggling(true);
    const next = !wf.active;
    setWf({ ...wf, active: next }); // optimistic
    try {
      await api.put(`/workflows/${id}`, { active: next });
      toast.success(next ? "Workflow activé" : "Workflow mis en brouillon");
    } catch (e) {
      setWf({ ...wf, active: !next }); // rollback
      toast.error(e?.response?.data?.detail || "Échec");
    } finally {
      setToggling(false);
    }
  };

  const testTrigger = async () => {
    setTestingTrigger(true);
    setTriggerDebug(null);
    try {
      const { data } = await api.post(`/workflows/${id}/test-trigger`);
      setTriggerDebug(data);
      if (data.status === "error") {
        toast.error(`Erreur : ${data.error}`);
      } else if ((data.new || 0) === 0 && (data.matched || 0) === 0) {
        toast.info(`Aucun email correspondant au filtre Gmail "${data.gmail_query_sent}".`);
      } else if ((data.new || 0) === 0) {
        toast.info(`${data.matched} email(s) trouvé(s) mais tous déjà traités. Marquez-en un comme non-lu dans Gmail pour retester.`);
      } else {
        toast.success(`${data.new} email(s) déclenché(s) → ${data.executions?.length || 0} exécution(s).`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally {
      setTestingTrigger(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Supprimer ce workflow ?")) return;
    await api.delete(`/workflows/${id}`);
    toast.success("Supprimé");
    navigate("/app/workflows");
  };

  if (!wf) return <div className="p-8 text-sm text-zinc-500">Chargement...</div>;

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="workflow-detail-page">
      <Link to="/app/workflows" className="flex items-center gap-2 text-sm text-zinc-500 hover:text-black mb-6">
        <ArrowLeft size={14}/> Retour
      </Link>

      <div className="flex items-start justify-between mb-8">
        <div className="min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <span className="overline">Workflow</span>
            <button
              onClick={toggleActive}
              disabled={toggling}
              title={wf.active ? "Cliquer pour mettre en brouillon" : "Cliquer pour activer"}
              data-testid="workflow-status-toggle"
              className={
                "transition-all rounded-sm font-mono text-[10px] px-2 py-1 cursor-pointer disabled:opacity-50 " +
                (wf.active
                  ? "klein-bg text-white hover:bg-blue-700"
                  : "bg-zinc-200 text-zinc-700 hover:bg-zinc-300")
              }
            >
              {toggling ? "..." : wf.active ? "✓ ACTIF — cliquer pour désactiver" : "○ BROUILLON — cliquer pour activer"}
            </button>
          </div>
          <h1 className="font-display text-4xl font-medium tracking-tighter">{wf.name}</h1>
          <p className="text-zinc-600 mt-2 max-w-2xl">{wf.description || "Pas de description"}</p>
        </div>
        <div className="flex items-center gap-2">
          {GMAIL_TRIGGER_ALIASES.has((wf.trigger?.type || "").toLowerCase()) && (
            <Button
              onClick={testTrigger}
              disabled={testingTrigger || !wf.active}
              variant="outline"
              className="rounded-sm"
              title={!wf.active ? "Activez le workflow d'abord" : "Polle Gmail maintenant et exécute pour chaque nouvel email correspondant"}
              data-testid="workflow-test-trigger-btn"
            >
              <Inbox size={14} className="mr-2"/> {testingTrigger ? "Test..." : "Tester le déclencheur"}
            </Button>
          )}
          <Link to={`/app/workflows/${id}/edit`}>
            <Button variant="outline" className="rounded-sm" data-testid="workflow-edit-btn"><Edit3 size={14} className="mr-2"/> Éditer</Button>
          </Link>
          <Button onClick={run} disabled={executing} className="rounded-sm klein-bg text-white hover:bg-blue-700" data-testid="workflow-run-btn">
            <Play size={14} className="mr-2"/> {executing ? "Exécution..." : "Exécuter"}
          </Button>
          <Button variant="outline" onClick={remove} className="rounded-sm border-red-200 text-red-600 hover:bg-red-50" data-testid="workflow-detail-delete">
            <Trash2 size={14}/>
          </Button>
        </div>
      </div>

      {/* Flow visualization */}
      <div className="bg-white border border-zinc-200 rounded-sm p-8 mb-8">
        <span className="overline">Configuration</span>
        <div className="mt-4 space-y-3">
          <div className="flex items-center gap-3 p-4 border border-zinc-200 rounded-sm bg-[#FAFAFA]">
            <div className="w-8 h-8 klein-bg text-white rounded-sm flex items-center justify-center font-mono text-xs shrink-0">⚡</div>
            <div>
              <p className="font-medium text-sm">Déclencheur</p>
              <p className="text-xs text-zinc-500 font-mono">{wf.trigger?.type}</p>
            </div>
          </div>
          {wf.steps?.map((s, i) => (
            <div key={s.id} className="flex items-center gap-3 p-4 border border-zinc-200 rounded-sm bg-[#FAFAFA]">
              <div className="w-8 h-8 bg-black text-white rounded-sm flex items-center justify-center font-mono text-xs shrink-0">{i + 1}</div>
              <div>
                <p className="font-medium text-sm">{s.name}</p>
                <p className="text-xs text-zinc-500 font-mono">{s.type}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Trigger debug panel (shown after clicking "Tester le déclencheur") */}
      {triggerDebug && (
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-6 mb-8" data-testid="trigger-debug-panel">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-lg font-medium tracking-tight">Diagnostic du déclencheur</h3>
            <button onClick={() => setTriggerDebug(null)} className="text-xs text-zinc-500 hover:text-black">✕ fermer</button>
          </div>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-xs font-mono">
            <div><dt className="text-zinc-500">Trigger type stocké :</dt><dd className="text-black">{triggerDebug.trigger_type_in_db || "(vide)"}</dd></div>
            <div><dt className="text-zinc-500">Status :</dt><dd className={triggerDebug.status === "error" ? "text-red-600" : "text-emerald-700"}>{triggerDebug.status}</dd></div>
            <div className="md:col-span-2"><dt className="text-zinc-500">Query Gmail envoyée :</dt><dd className="text-black bg-white border border-zinc-200 rounded-sm px-2 py-1 mt-0.5 break-all">{triggerDebug.gmail_query_sent}</dd></div>
            <div><dt className="text-zinc-500">subject_contains utilisé :</dt><dd>{triggerDebug.subject_contains_used || "(aucun)"}</dd></div>
            <div><dt className="text-zinc-500">from utilisé :</dt><dd>{triggerDebug.sender_filter_used || "(aucun)"}</dd></div>
            <div><dt className="text-zinc-500">Emails matchés (total) :</dt><dd>{triggerDebug.matched ?? 0}</dd></div>
            <div><dt className="text-zinc-500">Nouveaux (non encore traités) :</dt><dd>{triggerDebug.new ?? 0}</dd></div>
            <div><dt className="text-zinc-500">Types d&apos;étapes du workflow :</dt><dd>{(triggerDebug.step_types || []).join(", ") || "(aucune)"}</dd></div>
          </dl>
          {triggerDebug.error && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-sm">
              <p className="text-xs text-red-700 font-mono break-all">❌ {triggerDebug.error}</p>
            </div>
          )}
          {triggerDebug.matched_messages_summary?.length > 0 && (
            <div className="mt-3">
              <p className="text-xs text-zinc-500 font-mono mb-1">Emails Gmail trouvés :</p>
              <ul className="text-xs space-y-1">
                {triggerDebug.matched_messages_summary.map((m, i) => (
                  <li key={i} className="flex items-start gap-2 p-2 bg-white border border-zinc-200 rounded-sm">
                    <span className={m.already_processed ? "text-zinc-400" : "text-emerald-600"}>{m.already_processed ? "○" : "●"}</span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate"><span className="font-mono text-zinc-500">de</span> {m.from}</p>
                      <p className="truncate"><span className="font-mono text-zinc-500">objet</span> {m.subject}</p>
                    </div>
                    {m.already_processed && <span className="text-[10px] font-mono text-zinc-400 shrink-0">déjà traité</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {triggerDebug.executions?.length > 0 && (
            <div className="mt-3">
              <p className="text-xs text-zinc-500 font-mono mb-1">Exécutions lancées :</p>
              <ul className="text-xs space-y-2">
                {triggerDebug.executions.map((ex, i) => (
                  <li key={i} className="p-3 bg-white border border-zinc-200 rounded-sm">
                    <p className="font-medium truncate">{ex.subject}</p>
                    <p className={ex.status === "success" ? "text-emerald-700 text-xs mt-1" : "text-red-700 text-xs mt-1"}>
                      {ex.status === "success" ? "✓" : "✗"} {ex.status} {ex.error ? `— ${ex.error}` : ""}
                    </p>
                    {ex.step_logs_summary?.map((sl, j) => (
                      <p key={j} className="text-[11px] text-zinc-600 mt-1 ml-3 font-mono">
                        → [{sl.mode}] {sl.name} ({sl.type}) : <span className={sl.status === "success" ? "text-emerald-700" : "text-red-700"}>{sl.status}</span> {sl.message ? `— ${sl.message}` : ""}
                      </p>
                    ))}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Executions */}
      <div className="bg-white border border-zinc-200 rounded-sm">
        <div className="p-6 border-b border-zinc-200 flex items-center justify-between">
          <h2 className="font-display text-xl font-medium tracking-tight">Historique d&apos;exécutions</h2>
          <span className="text-xs text-zinc-500 font-mono">{execs.length} exécution(s)</span>
        </div>
        {execs.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500">Aucune exécution pour l&apos;instant.</div>
        ) : (
          <ul className="divide-y divide-zinc-200">
            {execs.map((e) => (
              <li key={e.id} className="p-5" data-testid={`execution-row-${e.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {e.status === "success" ? (
                      <CheckCircle2 size={16} className="text-emerald-600"/>
                    ) : (
                      <XCircle size={16} className="text-red-600"/>
                    )}
                    <span className="font-medium text-sm capitalize">{e.status}</span>
                  </div>
                  <span className="text-xs text-zinc-500 font-mono flex items-center gap-1">
                    <Clock size={12}/> {new Date(e.started_at).toLocaleString("fr-FR")}
                  </span>
                </div>
                <div className="pl-6 space-y-1">
                  {e.step_logs?.map((l, i) => (
                    <p key={i} className="text-xs text-zinc-500 font-mono">
                      <span className="text-emerald-600">✓</span> {l.name} · {l.type}
                    </p>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
